import { useState } from "react";
import { type Maker, send, useChange, useMakers } from "../api";
import { Dialog, DialogForm, ErrorNote } from "../components/ui";
import { useNotify } from "../notify";

/**
 * Item names read better with the maker in front of them (Scott, 2026-09-20): brewers say "Briess
 * Pilsen Malt", not "Pilsen Malt by Briess Malt & Ingredients". The alias comes from the catalog or
 * from the start of the supplier SKU (BM-PILS becomes "BM"), and every brewery can change it here,
 * because the shortening people use is a local habit. Clearing one drops the prefix altogether.
 *
 * The list stays behind a button (Tyler, 2026-09-20): it is set once and then left alone, so Settings
 * should not open onto every maker a brewery has ever stocked.
 */
export function MakerNames() {
  const { data: makers = [], isLoading, error } = useMakers();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  return (
    <section id="makers" className="section">
      <div className="section-header" style={{ cursor: "default" }}>
        <h2>Maker Aliases</h2>
        <span className="aside">{makers.length ? `${makers.length} makers` : ""}</span>
      </div>
      <div className="section-body stack">
        <p className="muted" style={{ margin: 0 }}>
          The short name shown in front of an item, for everyone at this brewery. Leave one empty to
          show the item's name on its own. An item you named yourself shows as you typed it.
        </p>
        <ErrorNote error={error} />
        <div className="row">
          <button
            type="button"
            className="button"
            onClick={() => setEditing((was) => !was)}
            aria-expanded={editing}
            aria-controls="maker-aliases"
          >
            {editing ? "Done" : "Edit Aliases"}
          </button>
          {editing ? (
            <button type="button" className="button danger" onClick={() => setConfirming(true)}>
              Reset Aliases
            </button>
          ) : null}
        </div>
        <div className={`reveal ${editing ? "open" : ""}`} id="maker-aliases">
          <div className="reveal-inner stack">
            {isLoading ? <p className="muted">Loading</p> : null}
            {!isLoading && makers.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                Nothing yet. Makers appear here as you add items.
              </p>
            ) : null}
            {makers.length > 0 ? (
              <div className="list-rows">
                {makers.map((m) => (
                  <MakerRow key={m.maker} maker={m} />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <ResetDialog open={confirming} onClose={() => setConfirming(false)} />
    </section>
  );
}

function MakerRow({ maker }: { maker: Maker }) {
  const notify = useNotify();
  const [shortName, setShortName] = useState(maker.shortName);
  const save = useChange(
    (name: string, key) => send.maker({ maker: maker.maker, shortName: name }, key),
    {
      onSuccess: (m) => notify(m.shortName ? `Items show as "${m.shortName} …"` : "Prefix removed"),
    },
  );
  const changed = shortName.trim() !== maker.shortName;

  return (
    <div>
      <form
        className="row"
        style={{ flex: 1, flexWrap: "wrap" }}
        onSubmit={(e) => {
          e.preventDefault();
          if (changed) save.mutate(shortName.trim());
        }}
      >
        <strong style={{ minWidth: 0, overflowWrap: "anywhere" }}>{maker.maker}</strong>
        <span className="muted">
          {maker.items} {maker.items === 1 ? "item" : "items"}
        </span>
        <span className="spacer" />
        <input
          className="input"
          style={{ maxWidth: 160 }}
          value={shortName}
          aria-label={`Short name for ${maker.maker}`}
          placeholder="No prefix"
          onChange={(e) => setShortName(e.target.value)}
        />
        <button
          type="submit"
          className="button small primary"
          disabled={!changed || save.isPending}
        >
          Save
        </button>
      </form>
      <ErrorNote error={save.error} />
    </div>
  );
}

function ResetDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const notify = useNotify();
  const reset = useChange((_: undefined, key) => send.resetMakers(key), {
    onSuccess: () => {
      notify("Aliases reset");
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} title="Reset aliases">
      <DialogForm
        onSubmit={() => reset.mutate(undefined)}
        footer={
          <>
            <button type="button" className="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="button danger-fill" disabled={reset.isPending}>
              Reset Aliases
            </button>
          </>
        }
      >
        <p style={{ margin: 0 }}>All of your aliases will be removed and reset to standard.</p>
        <p className="muted" style={{ margin: 0 }}>
          Standard is what the catalog calls each maker, or the start of the supplier SKU where it
          names none. Your items, stock and history are untouched.
        </p>
        <ErrorNote error={reset.error} />
      </DialogForm>
    </Dialog>
  );
}
